import React, { useState } from 'react';
import { SearchField } from '../../components/SearchField';
import { LicenseTable } from './LicenseTable';
import { ISettings } from './ColumnDefinitions';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
} from '@mui/material';
import { ExpandMore, FilterList } from '@mui/icons-material';
import { LicenseOverviewModel } from './ColumnDefinitions';
import { ExcelExport } from './ExcelExport';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { GridPaginationModel } from '@mui/x-data-grid-pro';

export interface Props {
  licenses: LicenseOverviewModel[];
  filter: any;
  report: any;
  setOnlyActive(val: boolean): void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 10.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};

function getStyles(name, personName, theme) {
  return {
    fontWeight: personName.indexOf(name) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
  };
}

interface SearchableLicenseTableProps {
  licenses: LicenseOverviewModel[];
  loading: boolean;
  exportLoading: boolean;
  loadExportLicenses(): Promise<LicenseOverviewModel[]>;
  rowCount: number;
  paginationModel: GridPaginationModel;
  setPaginationModel(val: GridPaginationModel | ((current: GridPaginationModel) => GridPaginationModel)): void;
  onlyActive: boolean;
  onlyLocked: boolean;
  onlyPendTerm: boolean;
  onlyADFS: boolean;
  onlyInformed: boolean;
  enableDateFilter: boolean;
  fromDate: moment.Moment | null;
  toDate: moment.Moment | null;
  setSearchTermParent(val: string);
  setOnlyActive(val: boolean): void;
  setOnlyLocked(val: boolean): void;
  setOnlyPendTerm(val: boolean): void;
  setOnlyADFS(val: boolean): void;
  setOnlyInformed(val: boolean): void;
  setEnableDateFilter(val: boolean): void;
  setFromDate(val: moment.Moment | null): void;
  setToDate(val: moment.Moment | null): void;
  setColumns(ids: boolean[]): void;
  columnlist: ISettings[];
  searchTermParent: string;
}

export function SearchableLicenseTable({
  licenses,
  loading,
  exportLoading,
  loadExportLicenses,
  rowCount,
  paginationModel,
  setPaginationModel,
  onlyActive,
  onlyLocked,
  onlyPendTerm,
  onlyADFS,
  onlyInformed,
  enableDateFilter,
  fromDate,
  toDate,
  setSearchTermParent,
  setOnlyActive,
  setOnlyLocked,
  setOnlyPendTerm,
  setOnlyADFS,
  setOnlyInformed,
  setEnableDateFilter,
  setFromDate,
  setToDate,
  setColumns,
  columnlist,
  searchTermParent,
}: SearchableLicenseTableProps) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState(searchTermParent);
  const [checkList, setCheckList] = useState(columnlist.map((c) => c.showColumn));
  const [licenseColumnsNameValues, setLicenseColumnsNameValues] = useState<string[]>(
    columnlist.filter((c) => c.showColumn === true).map((c) => c.name),
  );

  const handleChange = (event: SelectChangeEvent<string[]>, child?) => {
    const {
      target: { value },
    } = event;
    const id = child.props.value;
    const index = columnlist.findIndex((col) => col.name === id);
    const newArr = [...checkList];
    newArr[index] = !newArr[index];
    setCheckList(newArr);
    setLicenseColumnsNameValues(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClose = () => {
    setColumns(checkList);
  };

  const handleSearchTerm = (value: string) => {
    setSearchTerm(value);
    setSearchTermParent(value);
  };

  // Calculate active filter count
  const activeFilterCount = [onlyActive, onlyLocked, onlyPendTerm, onlyADFS, onlyInformed, enableDateFilter].filter(Boolean).length;

  return (
    <div>
      <Accordion sx={{ mb: 2, marginTop: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <FilterList />
            <Typography>Filtre</Typography>
            {activeFilterCount > 0 && <Chip label={activeFilterCount} size="small" color="primary" />}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            {/* Boolean Filters - Vertical */}
            <FormControlLabel control={<Switch onChange={() => setOnlyActive(!onlyActive)} checked={onlyActive} />} label="Kun aktive" />
            <FormControlLabel control={<Switch onChange={() => setOnlyLocked(!onlyLocked)} checked={onlyLocked} />} label="Kun låste" />
            <FormControlLabel
              control={<Switch onChange={() => setOnlyPendTerm(!onlyPendTerm)} checked={onlyPendTerm} />}
              label="Kun under oppsigelse"
            />
            <FormControlLabel control={<Switch onChange={() => setOnlyADFS(!onlyADFS)} checked={onlyADFS} />} label="ADFS" />
            <FormControlLabel
              control={<Switch onChange={() => setOnlyInformed(!onlyInformed)} checked={onlyInformed} />}
              label="Kun informet for ekstra bruker"
            />

            <FormControlLabel
              control={<Switch onChange={() => setEnableDateFilter(!enableDateFilter)} checked={enableDateFilter} />}
              label="Filtrer etter registreringsdato"
            />
            {enableDateFilter && (
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <DatePicker label="Fra dato" value={fromDate} onChange={setFromDate} maxDate={toDate || undefined} />
                <DatePicker label="Til dato" value={toDate} onChange={setToDate} minDate={fromDate || undefined} />
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <SearchField filter={handleSearchTerm} parentSearchTerm={searchTerm} debounceMs={900} minSearchLength={2} />

      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" sx={{ alignItems: 'center' }}></Stack>
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <div>
            <FormControl sx={{ m: 1, width: 300 }}>
              <InputLabel id="demo-multiple-name-label">Valgte kolonner: </InputLabel>
              <Select
                sx={{ boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                multiple
                value={licenseColumnsNameValues}
                onChange={handleChange}
                onClose={handleClose}
                input={<OutlinedInput label="Valgte kolonner:" />}
                renderValue={(selected) => selected.join(', ')}
                MenuProps={MenuProps}
              >
                {columnlist.map((column, index) => (
                  <MenuItem key={column.name} value={column.name} style={getStyles(column.name, licenseColumnsNameValues, theme)}>
                    <Checkbox checked={checkList[index]} />
                    <ListItemText primary={column.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <ExcelExport items={licenses} columnlist={columnlist} loadItems={loadExportLicenses} loading={exportLoading} />
          <Typography variant="caption">Antall: {rowCount.toLocaleString('nb-NO')}</Typography>
        </Stack>
      </Stack>

      <LicenseTable
        personName={licenseColumnsNameValues}
        items={columnlist}
        licenses={licenses}
        loading={loading}
        rowCount={rowCount}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
      />
    </div>
  );
}
